/**
 * A port of https://github.com/HubSpot/humanize to typescript and tree-shake support
 */

const TIME_FORMATS = [
  {
    name: 'second',
    value: 1e3,
  },
  {
    name: 'minute',
    value: 6e4,
  },
  {
    name: 'hour',
    value: 36e5,
  },
  {
    name: 'day',
    value: 864e5,
  },
  {
    name: 'week',
    value: 6048e5,
  },
];

const LABELS_FOR_POWERS_OF_KILO = {
  P: Math.pow(2, 50),
  T: Math.pow(2, 40),
  G: Math.pow(2, 30),
  M: Math.pow(2, 20),
};

const FIXED_OCCURANCE_TIMES = ['never', 'once', 'twice'];

//------------------------------------------------------------------------------
// Helpers
//------------------------------------------------------------------------------

const exists = (maybe) => typeof maybe !== 'undefined' && maybe !== null;

const isNaN = (value) => value !== value; // eslint-disable-line

/**
 * Converts an integer into its most compact representation
 *
 * ```
 * compactInteger(123456789, 1)
 * // "123.5M"
 *
 * // Switch to scientific notation for trillons, because no one knows those abbreviations.
 * compactInteger(-7832186132456328967, 4)
 * // "-7.8322x10^18"
 *
 * compactInteger(-100, 2)
 * // "-100"
 * ```
 */
export function compactInteger(input: number, decimals = 0, forceDecimal = true): string {
  decimals = Math.max(decimals, 0);
  const numberLengths = [13, 10, 7, 4];
  const bigNumPrefixes = ['T', 'B', 'M', 'k'];

  // small numbers
  if (Math.abs(input) < 1000) {
    return Math.floor(input) === input ? String(input) : input.toFixed(decimals);
  }

  // really big numbers
  if (String(Math.round(input)).length > numberLengths[0] + 3) {
    return input.toExponential(decimals).replace('e+', 'x10^');
  }

  const signString = input < 0 ? '-' : '';
  const unsignedNumber = Math.round(Math.abs(input));
  const unsignedNumberString = String(unsignedNumber);
  const numberLength = unsignedNumberString.length;

  // 999 < unsignedNumber < 999,999,999,999,999
  let length;
  for (let i = 0; i < numberLengths.length; i++) {
    const _length = numberLengths[i];
    if (numberLength >= _length) {
      length = _length;
      break;
    }
  }

  const decimalIndex = numberLength - length + 1;
  const unsignedNumberCharacterArray = unsignedNumberString.split('');

  const wholePartArray = unsignedNumberCharacterArray.slice(0, decimalIndex);
  const decimalPartArray = unsignedNumberCharacterArray.slice(decimalIndex, decimalIndex + decimals);

  const wholePart = wholePartArray.join('');

  // pad decimalPart if necessary
  let decimalPart = decimalPartArray.join('');
  if (decimalPart.length < decimals) {
    decimalPart += `${Array(decimals - decimalPart.length + 1).join('0')}`;
  }

  let output;
  if (decimals === 0) {
    output = `${signString}${wholePart}${bigNumPrefixes[numberLengths.indexOf(length)]}`;
  } else {
    let outputNumber: string | number = Number(`${wholePart}.${decimalPart}`);
    if (forceDecimal) {
      outputNumber = outputNumber.toFixed(decimals);
    }
    output = `${signString}${outputNumber}${bigNumPrefixes[numberLengths.indexOf(length)]}`;
  }

  return output;
}

/**
 * Formats the value like a 'human-readable' file size (i.e. '13 KB', '4.1 MB', '102 bytes', etc).
 */
export function fileSize(filesize: number, precision = 2): string {
  for (const label in LABELS_FOR_POWERS_OF_KILO) {
    if (LABELS_FOR_POWERS_OF_KILO.hasOwnProperty(label)) {
      const minnum = LABELS_FOR_POWERS_OF_KILO[label];
      if (filesize >= minnum) {
        return `${formatNumber(filesize / minnum, precision, '')} ${label}B`;
      }
    }
  }
  if (filesize >= 1024) {
    return `${formatNumber(filesize / 1024, 0)} KB`;
  }

  return formatNumber(filesize, 0) + pluralize(filesize, ' byte');
}

// Formats a number to a human-readable string.
// Localize by overriding the precision, thousand and decimal arguments.
export function formatNumber(number: number, precision = 0, thousand = ',', decimal = '.') {
  // Create some private utility functions to make the computational
  // code that follows much easier to read.
  const firstComma = (_number, _thousand, _position) => {
    return _position ? _number.substr(0, _position) + _thousand : '';
  };

  const commas = (_number, _thousand, _position) => {
    return _number.substr(_position).replace(/(\d{3})(?=\d)/g, `$1${_thousand}`);
  };

  const decimals = (_number, _decimal, usePrecision) => {
    return usePrecision ? _decimal + toFixed(Math.abs(_number), usePrecision).toString().split('.')[1] : '';
  };

  const usePrecision = normalizePrecision(precision);

  // Do some calc
  const negative = (number < 0 && '-') || '';
  const base = String(toFixed(Math.abs(number || 0), usePrecision));
  const mod = base.length > 3 ? base.length % 3 : 0;

  // Format the number
  return (
    negative + firstComma(base, thousand, mod) + commas(base, thousand, mod) + decimals(number, decimal, usePrecision)
  );
}

/**
 * Fixes binary rounding issues (eg. (0.615).toFixed(2) === '0.61')
 */
export function toFixed(value: number, precision: number): number {
  precision = exists(precision) ? precision : normalizePrecision(precision, 0);
  const power = Math.pow(10, precision);

  // Multiply up by precision, round accurately, then divide and use native toFixed()
  return parseInt((Math.round(value * power) / power).toFixed(precision), 10);
}

/**
 * Ensures precision value is a positive integer
 */
export function normalizePrecision(value: number, base?: number): number {
  value = Math.round(Math.abs(value));
  return isNaN(value) ? base : value;
}

/**
 * Converts an integer to its ordinal as a string.
 * e.g 22 => 22nd
 */
export function ordinal(value: number): string {
  if (value === 0) {
    return value.toString();
  }

  const specialCase = value % 100;
  if ([11, 12, 13].indexOf(specialCase) >= 0) {
    return `${value}th`;
  }

  const leastSignificant = value % 10;

  let end;
  switch (leastSignificant) {
    case 1:
      end = 'st';
      break;
    case 2:
      end = 'nd';
      break;
    case 3:
      end = 'rd';
      break;
    default:
      end = 'th';
  }

  return `${value}${end}`;
}

/**
 * Interprets numbers as occurences. Also accepts an optional array/map of overrides.
 * e.g.:
 *   * 0 => never
 *   * 1 => once
 *   * 1.1. => 1.1 times
 *   * 2 => twice
 */
export function times(value: number, overrides?: { [key: number]: string }): string {
  if (isFinite(value) && value >= 0) {
    return (overrides && overrides[value]) || FIXED_OCCURANCE_TIMES[value] || `${value} times`;
  }
  return null;
}

/**
 * Returns the plural version of a given word if the value is not 1. The default suffix is 's'.
 */
export function pluralize(count: number, singular: string, plural?: string): string {
  return count === 1 ? singular : plural || `${singular}s`;
}

/**
 * Truncates a string if it is longer than the specified number of characters (inclusive).
 * Truncated strings will end with a translatable ellipsis sequence ("…").
 *
 * ```
 * truncate('long text is good for you')
 * // "long text is good for you"
 *
 * truncate('long text is good for you', 19)
 * // "long text is goo..."
 *
 * truncate('long text is good for you', 19, '... etc')
 * // "long text is... etc"
 * ```
 */
export function truncate(str: string, length = 100, ending = '...'): string {
  return str.length > length ? str.substring(0, length - ending.length) + ending : str;
}

/**
 * Truncates a string after a certain number of words.
 *
 * ```
 * truncateWords('long text is good for you', 5)
 * // "long text is good for ..."
 * ```
 */
export function truncateWords(string: string, length: number): string {
  const array = string.split(' ');
  let result = '';
  let i = 0;

  while (i < length) {
    if (exists(array[i])) {
      result += `${array[i]} `;
    }
    i++;
  }

  if (array.length > length) {
    return `${result}...`;
  }

  return null;
}

/**
 * Truncates a number to an upper bound.
 *
 * ```
 * boundedNumber(110, 100)
 * // "100+"
 *
 * boundedNumber(50, 100)
 * // "50"
 * ```
 */
export function boundedNumber(num: number, bound = 100, ending = '+'): string {
  let result;

  if (isFinite(num) && isFinite(bound)) {
    if (num > bound) {
      result = bound + ending;
    }
  }

  return (result || num).toString();
}

/**
 * Converts a list of items to a human readable string with an optional limit.
 *
 * ```
 * items = ['apple', 'orange', 'banana', 'pear', 'pineapple']
 *
 * oxford(items)
 * // "apple, orange, banana, pear, and pineapple"
 *
 * oxford(items, 3)
 * // "apple, orange, banana, and 2 others"
 *
 * // Pluralizes properly too!
 * oxford(items, 4)
 * // "apple, orange, banana, pear, and 1 other"
 *
 * oxford(items, 3, "and some other fruits")
 * // "apple, orange, banana, and some other fruits"
 * ```
 */
export function oxford(items: Array<string | number>, limit?: number, limitStr?: string): string {
  const numItems = items.length;

  let limitIndex;
  if (numItems < 2) {
    return String(items);
  } else if (numItems === 2) {
    return items.join(' and ');
  } else if (exists(limit) && numItems > limit) {
    const extra = numItems - limit;
    limitIndex = limit;
    limitStr = exists(limitStr) ? limitStr : `, and ${extra} ${pluralize(extra, 'other')}`;
  } else {
    limitIndex = -1;
    limitStr = `, and ${items[numItems - 1]}`;
  }

  return items.slice(0, limitIndex).join(', ') + limitStr;
}

/**
 * Describes how many times an item appears in a list
 */
export function frequency(list: any[], verb: string) {
  if (!Array.isArray(list)) {
    return null;
  }

  const len = list.length;
  const _times = times(len);

  return len === 0 ? `${_times} ${verb}` : `${verb} ${_times}`;
}

/**
 * Matches a pace (value and interval) with a logical time frame. Very useful for slow paces.
 *
 * ```
 * pace(1.5, second, "heartbeat")
 * // Approximately 2 heartbeats per second
 *
 * pace(4, week)
 * // Approximately 4 times per week
 *
 * pace(1, decade, "life crisis")
 * // Less than 1 life crisis per week
 * ```
 */
export function pace(value: number, intervalMs: number, unit = 'time') {
  if (value === 0 || intervalMs === 0) {
    // Needs a better string than this...
    return `No ${pluralize(0, unit)}`;
  }

  // Expose these as overridables?
  let prefix = 'Approximately';
  let timeUnit;
  let relativePace;

  const rate = value / intervalMs;
  for (let i = 0; i < TIME_FORMATS.length; ++i) {
    // assumes sorted list
    const f = TIME_FORMATS[i];
    relativePace = rate * f.value;
    if (relativePace > 1) {
      timeUnit = f.name;
      break;
    }
  }

  // Use the last time unit if there is nothing smaller
  if (!timeUnit) {
    prefix = 'Less than';
    relativePace = 1;
    timeUnit = TIME_FORMATS[TIME_FORMATS.length - 1].name;
  }

  const roundedPace = Math.round(relativePace);
  unit = pluralize(roundedPace, unit);

  return `${prefix} ${roundedPace} ${unit} per ${timeUnit}`;
}
